import { NsbFlavorsPrecomputes } from '@api/SmartBaselineFlavorsApi';
import { FLAVOR_DEFS, RECOMMENDED_APPLIED_LABEL_KEY } from './flavorDefs';

/**
 * Resolve a flavour selection to its id, label and price for one row's precomputes.
 *
 * Lives in a leaf module rather than beside the cell that first needed it: the save path derives prices
 * too, and importing it from the renderer dragged the whole renderer graph — quick picker, baseline cell,
 * their store registrations — into `LineItemsProvider`, which broke every suite that mocks those stores.
 *
 * Returns the resolved flavour `id` alongside the label and price. The id is what the save path records,
 * and it cannot be recovered from the label: the recommended branch falls back to a literal string that
 * matches no FLAVOR_DEF, so a label->id lookup would silently record nothing and the row would come back
 * sourceless and be re-derived on the next load.
 */
export const getEffectivePriceForFlavor = (
  prec: NsbFlavorsPrecomputes,
  flavorId: string,
): { id: string | null; label: string; price: number | null } => {
  if (flavorId === 'recommended') {
    const price = prec.recommended_price ?? (prec.recommended_flavor === 'F3-5' ? prec.hist_avg_price ?? null : null);
    const def = FLAVOR_DEFS.find(f => f.id === prec.recommended_flavor || (prec.recommended_flavor === 'F3-5' && f.id === 'F3'));
    return { id: def?.id ?? null, label: def?.label ?? RECOMMENDED_APPLIED_LABEL_KEY, price };
  }
  const def = FLAVOR_DEFS.find(f => f.id === flavorId);
  return { id: def?.id ?? null, label: def?.label ?? '', price: def ? (prec[def.priceKey] as number | null) ?? null : null };
};

/**
 * The same resolution the CELL performs: a selected flavour with no price for this item falls back to
 * recommended.
 *
 * Sparse flavours are the reason -- F6 trend_projected, F7 qty_adjusted, F8 fred_adjusted are absent for
 * plenty of items. Without this the drawn row shows the recommended price and the undrawn row saved
 * nothing, which is the render/save divergence this module exists to close, narrowed to those flavours.
 * Both paths call this so neither can drift from the other again.
 */
export const resolveWithRecommendedFallback = (
  prec: NsbFlavorsPrecomputes,
  flavorId: string,
): { id: string | null; label: string; price: number | null } => {
  const first = getEffectivePriceForFlavor(prec, flavorId);
  if (first.price != null || flavorId === 'recommended') return first;
  return getEffectivePriceForFlavor(prec, 'recommended');
};
